import React, { useState } from "react";
import { Link } from "react-router-dom";
import Spinner from "../components/common/Spinner";
import AuthCard from "./AuthCard";
import analytics from "../services/analytics";
import { bookDemo } from "../api/studioAPI";
import { colors, typography, buttons, form } from "../styles/index";

const TEAM_SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const EMPTY = { name: "", email: "", company: "", team_size: "", notes: "", website: "" };

// /book-demo - a sales lead (backend routers/demo_request_api.py). "website" is hidden: people never fill it, bots do.
export default function BookDemoPage() {
  const [lead, setLead] = useState(EMPTY);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (key) => (e) => setLead((l) => ({ ...l, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return undefined;
    setError("");
    if (!lead.name.trim() || !lead.company.trim()) return setError("Please tell us your name and company.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) return setError("Please enter a valid work email address.");
    setLoading(true);
    try {
      await bookDemo({ ...lead, email: lead.email.trim(), team_size: lead.team_size || null, notes: lead.notes || null });
      analytics.event("demo_requested");
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    return undefined;
  };

  if (sent) {
    return (
      <AuthCard subtitle="Book a demo">
        <div role="status" style={{ ...typography.small, background: colors.primarySoft, padding: 12, borderRadius: 8 }}>
          Thanks - we&rsquo;ll be in touch within one working day to find a time.
        </div>
        <p style={{ ...typography.small, marginTop: 20, textAlign: "center", color: colors.textMuted }}>
          Can&rsquo;t wait? <Link to="/try-demo">Try the demo now</Link>.
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard subtitle="Book a demo" wide>
      <p style={{ ...typography.small, color: colors.textMuted }}>
        We&rsquo;ll walk you through turning one of your own procedures into a training simulation.
      </p>
      <form onSubmit={handleSubmit}>
        {[["name", "Your name", "text", "Jane Doe", "name"], ["email", "Work email", "email", "you@company.com", "email"],
          ["company", "Company", "text", "Your company", "organization"]].map(([key, label, type, placeholder, auto]) => (
          <div key={key} style={{ marginBottom: 16 }}>
            <label style={form.label} htmlFor={`bd-${key}`}>{label}</label>
            <input id={`bd-${key}`} type={type} autoComplete={auto} style={form.input} disabled={loading} value={lead[key]}
              onChange={set(key)} placeholder={placeholder} maxLength={100} />
          </div>
        ))}
        <div style={{ marginBottom: 16 }}>
          <label style={form.label} htmlFor="bd-team">How many people would you train? (optional)</label>
          <select id="bd-team" style={form.input} disabled={loading} value={lead.team_size} onChange={set("team_size")}>
            <option value="">-</option>
            {TEAM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={form.label} htmlFor="bd-notes">What would you like to train? (optional)</label>
          <textarea id="bd-notes" rows={3} style={{ ...form.input, resize: "vertical" }} disabled={loading} value={lead.notes}
            onChange={set("notes")} maxLength={1000} placeholder="Forklift safety, closing-up procedures, lab prep..." />
        </div>
        <div aria-hidden="true" style={{ position: "absolute", left: -10000, width: 1, height: 1, overflow: "hidden" }}>
          <label htmlFor="bd-website">Website</label>
          <input id="bd-website" tabIndex={-1} autoComplete="off" value={lead.website} onChange={set("website")} />
        </div>
        {error && <div style={{ ...form.error, marginBottom: 12 }} role="alert">{error}</div>}
        <button type="submit" disabled={loading} style={{ ...buttons.primary, width: "100%", opacity: loading ? 0.7 : 1 }}>
          {loading ? <><Spinner size={14} /> Sending...</> : "Book a demo"}
        </button>
        <p style={{ ...typography.small, marginTop: 20, textAlign: "center", color: colors.textMuted }}>
          See our <Link to="/privacy">Privacy Policy</Link> for how we use these details.
        </p>
      </form>
    </AuthCard>
  );
}
